import React, { useState, useEffect } from 'react';
import {
  Modal, Card, Row, Col, Tooltip, Input, Space, Typography, Button, Empty, Spin
} from 'antd';
import {
  ThunderboltOutlined,
  WalletOutlined,
  HomeOutlined,
  CreditCardOutlined,
  SafetyOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  StarOutlined,
  RightOutlined
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import CommonBillPayment from './commonBillPayment';
import MobileRecharge from './mobileRecharge';
import './UtilityPayment.css';
import CommingSoon from '../../container/commingsoon';
import { postRequest } from '../../Utils/AppApi';
import { INSTANTPAY_CATEGORIES } from '../../Utils/appConstants';
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';

const { Text } = Typography;

// Brand colors
const PRIMARY_COLOR = '#C9262C';
const ACCENT_COLOR = '#FFDD09';

// Categories to exclude from display
const EXCLUDED_CATEGORIES = ['Credit Card', 'C15'];

// Static categories for display (will be replaced by API data)
const STATIC_CATEGORIES = [
  { cat_id: "C00", category_name: "Mobile (Prepaid)", categoryKey: "C00" },
  { cat_id: "C01", category_name: "Mobile (Postpaid)", categoryKey: "C01" },
  { cat_id: "C02", category_name: "Landline", categoryKey: "C02" },
  { cat_id: "C03", category_name: "DTH", categoryKey: "C03" },
  { cat_id: "C04", category_name: "Electricity", categoryKey: "C04" },
  { cat_id: "C05", category_name: "Broadband", categoryKey: "C05" },
  { cat_id: "C06", category_name: "Cable TV", categoryKey: "C06" },
  { cat_id: "C07", category_name: "Gas (PNG)", categoryKey: "C07" },
  { cat_id: "C08", category_name: "Water", categoryKey: "C08" },
  { cat_id: "C09", category_name: "Education Fee", categoryKey: "C09" },
  { cat_id: "C10", category_name: "FASTag Recharge", categoryKey: "C10" },
  { cat_id: "C11", category_name: "Insurance", categoryKey: "C11" },
  { cat_id: "C12", category_name: "Donation", categoryKey: "C12" },
  { cat_id: "C13", category_name: "Loan EMI", categoryKey: "C13" },
  { cat_id: "C14", category_name: "Gas (LPG Cylinder)", categoryKey: "C14" },
  // { cat_id: "C15", category_name: "Credit Card", categoryKey: "C15" }, // Commented out - Credit Card excluded
  { cat_id: "C17", category_name: "Housing Society", categoryKey: "C17" },
  { cat_id: "C18", category_name: "Subscription", categoryKey: "C18" },
  { cat_id: "C19", category_name: "Municipal Taxes & Services", categoryKey: "C19" },
  { cat_id: "C20", category_name: "Recurring Deposit", categoryKey: "C20" },
  { cat_id: "C21", category_name: "Hospitals & Pathology", categoryKey: "C21" },
  { cat_id: "C22", category_name: "Clubs & Associations", categoryKey: "C22" },
  { cat_id: "C24", category_name: "Rental", categoryKey: "C24" },
  { cat_id: "C28", category_name: "NCMC Recharge", categoryKey: "C28" },
  { cat_id: "C29", category_name: "Electricity (Prepaid)", categoryKey: "C29" },
  { cat_id: "C30", category_name: "National Pension Scheme (NPS)", categoryKey: "C30" },
  { cat_id: "C31", category_name: "eChallan", categoryKey: "C31" }
];

function UtilityPayment(props) {
  const history = useHistory();
  const [selectCategory, setSelectCategory] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [commingSoon, setCommingSoon] = useState(false);
  const [categories, setCategories] = useState(STATIC_CATEGORIES);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [apiProvider] = useState('IP'); // Always InstantPay

  // Function to filter out excluded categories
  const filterExcludedCategories = (categoriesList) => {
    return categoriesList.filter(category => 
      !EXCLUDED_CATEGORIES.includes(category.category_name) && 
      !EXCLUDED_CATEGORIES.includes(category.categoryKey) &&
      !EXCLUDED_CATEGORIES.includes(category.cat_id)
    );
  };

  // Fetch categories from InstantPay API
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await postRequest(INSTANTPAY_CATEGORIES, {}, history);
      if (response.success && response.data) {
        // Map API response to category format
        let mappedCategories = response.data.map(cat => ({
          cat_id: cat.categoryKey,
          category_name: cat.categoryName,
          categoryKey: cat.categoryKey,
          iconUrl: cat.iconUrl,
          billerList: cat.billerList
        }));
        
        // Filter out excluded categories
        mappedCategories = filterExcludedCategories(mappedCategories);
        setCategories(mappedCategories);
      } else {
        // Fallback to static categories if API fails
        console.warn('Using static categories as fallback');
        // Filter static categories as well
        setCategories(filterExcludedCategories(STATIC_CATEGORIES));
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Keep static categories as fallback and filter them
      setCategories(filterExcludedCategories(STATIC_CATEGORIES));
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const handleOpenClose = () => setCommingSoon(false);

  const billerCategory = categories;

  const handleService = (category, cat_id) => {
    // For Prepaid and DTH, open the recharge page
    if (category === 'Mobile (Prepaid)' || category === 'DTH') {
      setSelectCategory(category);
      setSelectedCategoryId(cat_id);
      return;
    }
    setSelectCategory(category);
    setSelectedCategoryId(cat_id);
  };

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toUpperCase();
    if (name.includes('ELECTRICITY')) return <ThunderboltOutlined style={{ fontSize: '28px' }} />;
    if (name.includes('WATER')) return <i className="fas fa-tint" style={{ fontSize: '28px' }} />;
    if (name.includes('GAS') || name.includes('LPG')) return <i className="fas fa-fire" style={{ fontSize: '28px' }} />;
    if (name.includes('BROADBAND')) return <i className="fas fa-wifi" style={{ fontSize: '28px' }} />;
    if (name.includes('MOBILE') || name.includes('PREPAID') || name.includes('POSTPAID')) return <i className="fas fa-mobile-alt" style={{ fontSize: '28px' }} />;
    if (name.includes('FASTAG')) return <i className="fas fa-tag" style={{ fontSize: '28px' }} />;
    if (name.includes('INSURANCE')) return <SafetyOutlined style={{ fontSize: '28px' }} />;
    if (name.includes('CREDIT') || name.includes('EMI') || name.includes('LOAN')) return <CreditCardOutlined style={{ fontSize: '28px' }} />;
    if (name.includes('DTH')) return <i className="fas fa-tv" style={{ fontSize: '28px' }} />;
    if (name.includes('LANDLINE')) return <i className="fas fa-phone" style={{ fontSize: '28px' }} />;
    if (name.includes('CABLE')) return <i className="fas fa-plug" style={{ fontSize: '28px' }} />;
    if (name.includes('ECHALLAN') || name.includes('TRAFFIC')) return <i className="fas fa-car" style={{ fontSize: '28px' }} />;
    if (name.includes('HOSPITAL')) return <i className="fas fa-hospital" style={{ fontSize: '28px' }} />;
    if (name.includes('VOUCHER')) return <i className="fas fa-gift" style={{ fontSize: '28px' }} />;
    if (name.includes('MUNICIPAL')) return <i className="fas fa-building" style={{ fontSize: '28px' }} />;
    if (name.includes('EDUCATION')) return <i className="fas fa-graduation-cap" style={{ fontSize: '28px' }} />;
    if (name.includes('DONATION')) return <i className="fas fa-hand-holding-heart" style={{ fontSize: '28px' }} />;
    if (name.includes('RENTAL')) return <i className="fas fa-home" style={{ fontSize: '28px' }} />;
    if (name.includes('NPS') || name.includes('PENSION')) return <i className="fas fa-chart-line" style={{ fontSize: '28px' }} />;
    return <HomeOutlined style={{ fontSize: '28px' }} />;
  };

  const getCategoryColor = (categoryName) => {
    const name = categoryName.toUpperCase();
    if (name.includes('ELECTRICITY')) return '#ff6b6b';
    if (name.includes('WATER')) return '#4dabf7';
    if (name.includes('GAS') || name.includes('LPG')) return '#ff922b';
    if (name.includes('BROADBAND')) return '#9775fa';
    if (name.includes('PREPAID') || name.includes('POSTPAID') || name.includes('MOBILE')) return '#51cf66';
    if (name.includes('FASTAG')) return '#fcc419';
    if (name.includes('INSURANCE')) return '#339af0';
    if (name.includes('EMI') || name.includes('CREDIT') || name.includes('LOAN')) return '#f06595';
    if (name.includes('DTH')) return '#be4bdb';
    if (name.includes('LANDLINE')) return '#20c997';
    if (name.includes('CABLE')) return '#fd7e14';
    if (name.includes('ECHALLAN') || name.includes('TRAFFIC')) return '#e03131';
    if (name.includes('HOSPITAL')) return '#12b886';
    if (name.includes('VOUCHER')) return '#f59f00';
    if (name.includes('MUNICIPAL')) return '#5f3dc4';
    if (name.includes('EDUCATION')) return '#3b82f6';
    if (name.includes('DONATION')) return '#ec489a';
    if (name.includes('RENTAL')) return '#8b5cf6';
    if (name.includes('NPS') || name.includes('PENSION')) return '#06b6d4';
    return '#868e96';
  };

  const filteredCategories = billerCategory.filter(category =>
    category.category_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getGroupedCategories = () => {
    const groups = {
      'Quick Recharge': ['MOBILE (PREPAID)', 'MOBILE (POSTPAID)', 'FASTAG RECHARGE', 'DTH', 'NCMC RECHARGE'],
      'Home Utilities': ['ELECTRICITY', 'WATER', 'BROADBAND', 'LANDLINE', 'CABLE TV', 'GAS (PNG)', 'GAS (LPG CYLINDER)', 'ELECTRICITY (PREPAID)'],
      'Financial & Legal': ['INSURANCE', 'LOAN EMI', 'RECURRING DEPOSIT', 'NATIONAL PENSION SCHEME (NPS)', 'ECHALLAN', 'MUNICIPAL TAXES & SERVICES'],
      'Other Services': ['EDUCATION FEE', 'DONATION', 'HOUSING SOCIETY', 'SUBSCRIPTION', 'HOSPITALS & PATHOLOGY', 'CLUBS & ASSOCIATIONS', 'RENTAL']
    };

    const categorized = {
      'Quick Recharge': [],
      'Home Utilities': [],
      'Financial & Legal': [],
      'Other Services': []
    };

    filteredCategories.forEach(category => {
      const categoryName = category.category_name.toUpperCase();
      let placed = false;

      for (const [group, keywords] of Object.entries(groups)) {
        if (keywords.some(keyword => categoryName.includes(keyword))) {
          categorized[group].push(category);
          placed = true;
          break;
        }
      }

      if (!placed) {
        categorized['Other Services'].push(category);
      }
    });

    return Object.entries(categorized).filter(([_, services]) => services.length > 0);
  };

  const categoryGroups = getGroupedCategories();

  return (
    <div className="bbps-modern-container" style={{ margin: 0, padding: 0 }}>
      <style>{`
        .bbps-modern-search .bbps-search-input-modern:focus {
          border-color: ${PRIMARY_COLOR} !important;
          box-shadow: 0 0 0 2px ${PRIMARY_COLOR}20 !important;
        }
        .bbps-modern-service-card {
          transition: all 0.3s;
        }
        .bbps-modern-service-card:hover {
          border-color: ${PRIMARY_COLOR} !important;
          transform: translateY(-4px);
          box-shadow: 0 8px 20px ${PRIMARY_COLOR}20;
        }
        .bbps-modern-service-card .bbps-modern-service-tag {
          background: ${PRIMARY_COLOR} !important;
        }
        .bbps-modern-back-btn {
          background: ${PRIMARY_COLOR} !important;
          border-color: ${PRIMARY_COLOR} !important;
        }
        .bbps-modern-back-btn:hover {
          background: ${PRIMARY_COLOR}dd !important;
        }
        .ant-btn-primary {
          background: ${PRIMARY_COLOR} !important;
          border-color: ${PRIMARY_COLOR} !important;
        }
        .ant-btn-primary:hover {
          background: ${PRIMARY_COLOR}dd !important;
          border-color: ${PRIMARY_COLOR}dd !important;
        }
        .bbps-group-icon-wrapper {
          color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-process .ant-steps-item-icon {
          background: ${PRIMARY_COLOR} !important;
          border-color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon {
          border-color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon {
          color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after {
          background: ${PRIMARY_COLOR} !important;
        }
        /* Remove any spacing from containers */
        .content-wrapper, .content, .container-fluid, .row, [class*="col-"] {
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
        .ant-card-body {
          padding-top: 0 !important;
        }
      `}</style>
      <div className="bbps-animated-bg">
        <div className="bbps-bg-shape bbps-shape-1"></div>
        <div className="bbps-bg-shape bbps-shape-2"></div>
        <div className="bbps-bg-shape bbps-shape-3"></div>
      </div>

      <div className="content-wrapper" style={{ padding: 0, margin: 0 }}>
        <div className="content" style={{ padding: 0, margin: 0 }}>
          <div className="container-fluid" style={{ padding: 0, margin: 0 }}>
            <Row style={{ margin: 0 }}>
              <Col span={24} style={{ padding: 0 }}>
                <Card className="bbps-modern-card" bordered={false} style={{ margin: 0, borderRadius: 0 }}>
                  {!selectCategory ? (
                    <>
                      {/* Header with logo on right */}
                      <div className="bbps-modern-search mb-5">
                        <div className="bbps-search-wrapper">
                          <SearchOutlined className="bbps-search-icon-modern" />
                          <input
                            type="text"
                            className="bbps-search-input-modern"
                            placeholder="Search for electricity, broadband, fastag or any bill..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          {searchTerm && (
                            <button
                              className="bbps-search-clear"
                              onClick={() => setSearchTerm('')}
                            >
                              ×
                            </button>
                          )}
                        </div>
                        {searchTerm && filteredCategories.length > 0 && (
                          <div className="bbps-search-results">
                            Found {filteredCategories.length} service(s)
                          </div>
                        )}
                      </div>

                      {loadingCategories ? (
                        <div style={{ textAlign: 'center', padding: '60px' }}>
                          <Spin size="large" tip="Loading categories..." />
                        </div>
                      ) : filteredCategories.length > 0 ? (
                        categoryGroups.map(([groupName, services]) => (
                          <div key={groupName} className="bbps-modern-group mb-5">
                            <div className="bbps-modern-group-header">
                              <div className="bbps-group-icon-wrapper">
                                {groupName === 'Quick Recharge' && <ThunderboltOutlined style={{ color: ACCENT_COLOR }} />}
                                {groupName === 'Home Utilities' && <HomeOutlined style={{ color: ACCENT_COLOR }} />}
                                {groupName === 'Financial & Legal' && <WalletOutlined style={{ color: ACCENT_COLOR }} />}
                                {groupName === 'Other Services' && <StarOutlined style={{ color: ACCENT_COLOR }} />}
                              </div>
                              <h3 className="bbps-modern-group-title">{groupName}</h3>
                              <div className="bbps-group-divider"></div>
                              <span className="bbps-group-count">{services.length} services</span>
                            </div>

                            <Row gutter={[20, 20]} style={{ margin: 0 }}>
                              {services.map((category) => (
                                <Col xs={12} sm={8} md={6} lg={4} xl={3} key={category.cat_id} style={{ padding: 0 }}>
                                  <Tooltip title={`Click to pay ${category.category_name} bill`} placement="bottom">
                                    <div
                                      className="bbps-modern-service-card"
                                      onClick={() => handleService(category.category_name, category.cat_id)}
                                      style={{ '--card-color': getCategoryColor(category.category_name), margin: '8px' }}
                                    >
                                      <div className="bbps-card-glow"></div>
                                      <div className="bbps-modern-service-icon" style={{ background: `${getCategoryColor(category.category_name)}15` }}>
                                        {category.iconUrl ? (
                                          <img
                                            src={category.iconUrl}
                                            alt={category.category_name}
                                            className="bbps-service-icon-img-modern"
                                            style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                                          />
                                        ) : (
                                          <div className="bbps-icon-wrapper" style={{ color: getCategoryColor(category.category_name) }}>
                                            {getCategoryIcon(category.category_name)}
                                          </div>
                                        )}
                                      </div>
                                      <h5 className="bbps-modern-service-name">
                                        {category.category_name}
                                      </h5>
                                      <div className="bbps-modern-service-tag">
                                        Pay Now <RightOutlined style={{ color: ACCENT_COLOR, fontStyle: 'bold' }} />
                                      </div>
                                      <div className="bbps-card-hover-effect"></div>
                                    </div>
                                  </Tooltip>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        ))
                      ) : (
                        <Empty
                          description="No services found matching your search"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        >
                          <Button onClick={() => setSearchTerm('')} type="primary">
                            Clear Search
                          </Button>
                        </Empty>
                      )}
                    </>
                  ) : (
                    <div className="bbps-modern-payment-section" style={{ padding: 0 }}>
                      <Button
                        className="bbps-modern-back-btn"
                        onClick={() => {
                          setSelectCategory(null);
                          setSelectedCategoryId(null);
                        }}
                        icon={<ArrowLeftOutlined />}
                        style={{ marginBottom: 16, background: PRIMARY_COLOR, borderColor: PRIMARY_COLOR }}
                      >
                        Back to All Services
                      </Button>

                      {/* Show recharge component for Prepaid/DTH, otherwise show bill payment */}
                      {(selectCategory === 'Mobile (Prepaid)' || selectCategory === 'DTH') ? (
                        <MobileRecharge />
                      ) : (
                        <CommonBillPayment
                          category={selectCategory}
                          categoryId={selectedCategoryId}
                        />
                      )}
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </div>

      {/* Coming Soon Modal */}
      <Modal
        open={commingSoon}
        onCancel={handleOpenClose}
        footer={false}
        width={500}
        centered
      >
        <CommingSoon />
      </Modal>
    </div>
  );
}

export default UtilityPayment;